/**
 * /campaigns — list of campaigns + entry points to create-new and
 * dig into details. Becomes the new home of the dashboard after the
 * campaign-first redesign (#535).
 *
 * Brand-side: each campaign carries its product/audience/proof/CTA
 * + brand settings (voice, model, colors). The Brief tab inside a
 * campaign inherits everything; admins don't re-enter the same
 * context on every variant generation.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, apiJson } from '../lib/api';
import { EnvDiagnostic } from '../components/EnvDiagnostic';
import { ScreenshotsResync } from '../components/ScreenshotsResync';

interface Campaign {
  id: string;
  name: string;
  product: string;
  audience: string;
  offer: string;
  proof: string;
  default_cta: string;
  brand_settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export function Campaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiJson<{ campaigns: Campaign[] }>('/api/marketing/campaigns?limit=100');
      setCampaigns(res.campaigns);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaigns');
      setCampaigns([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Remove a campaign from the local list optimistically — the
  // delete request is in flight from the card itself.
  const onDeleted = useCallback((id: string) => {
    setCampaigns((cs) => (cs ? cs.filter((c) => c.id !== id) : cs));
  }, []);

  return (
    <div className="marketing-page">
      <header className="marketing-header">
        <div>
          <p className="eyebrow">Campaigns</p>
          <h1>Marketing campaigns</h1>
          <p>
            A campaign holds your brand context (product, audience, proof, voice, colors) plus its
            asset library. Briefs generated inside a campaign inherit everything.
          </p>
        </div>
        <Link to="/campaigns/new" className="btn btn-primary">
          New campaign
        </Link>
      </header>

      <EnvDiagnostic />

      <div style={{ marginTop: 12 }}>
        <ScreenshotsResync />
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginTop: 16 }}>
          {error}
        </div>
      )}

      {campaigns === null && (
        <div className="card" style={{ marginTop: 24, padding: 24 }}>
          <div className="spinner" />
        </div>
      )}

      {campaigns && campaigns.length === 0 && !error && (
        <div
          className="card"
          style={{
            marginTop: 24,
            padding: 32,
            textAlign: 'center',
            color: 'var(--color-text-muted)',
          }}
        >
          <p style={{ marginBottom: 16 }}>No campaigns yet.</p>
          <Link to="/campaigns/new" className="btn btn-primary">
            Create your first campaign
          </Link>
        </div>
      )}

      {campaigns && campaigns.length > 0 && (
        <div
          style={{
            marginTop: 24,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          {campaigns.map((c) => (
            <CampaignCard key={c.id} campaign={c} onDeleted={onDeleted} />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignCard({
  campaign,
  onDeleted,
}: {
  campaign: Campaign;
  onDeleted: (id: string) => void;
}) {
  const brand = campaign.brand_settings ?? {};
  const voiceId = typeof brand.voice_id === 'string' ? brand.voice_id : null;
  const model = typeof brand.model === 'string' ? brand.model : null;
  const primary = typeof brand.primary_color === 'string' ? brand.primary_color : null;

  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete(e: React.MouseEvent) {
    // Card root is a Link; the delete button is nested inside.
    // Without stopping propagation + preventing default, the click
    // navigates to the campaign before the confirm even fires.
    e.preventDefault();
    e.stopPropagation();
    const ok = window.confirm(
      `Delete "${campaign.name}"?\n\nPermanently removes the campaign, all its jobs, ` +
        'variants, assets, voiceovers, and rendered MP4s. Cannot be undone.',
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/marketing/campaigns/${campaign.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${body || 'delete_failed'}`);
      }
      onDeleted(campaign.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
    }
  }

  return (
    <Link
      to={`/campaigns/${campaign.id}`}
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 16,
        textDecoration: 'none',
        color: 'inherit',
        opacity: deleting ? 0.5 : 1,
        pointerEvents: deleting ? 'none' : 'auto',
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleting}
        title="Delete campaign"
        aria-label={`Delete campaign ${campaign.name}`}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 24,
          height: 24,
          borderRadius: 6,
          border: 'none',
          background: 'transparent',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {deleting ? '⋯' : '✕'}
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, paddingRight: 24 }}>
        <strong style={{ fontSize: 16, lineHeight: 1.2 }}>{campaign.name}</strong>
        {primary && (
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: primary,
              border: '1px solid rgba(255,255,255,0.15)',
              flexShrink: 0,
              marginTop: 4,
            }}
            title={`Brand color ${primary}`}
          />
        )}
      </div>
      {error && (
        <div className="alert alert-error" style={{ fontSize: 11 }}>
          {error}
        </div>
      )}
      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: 'var(--color-text-muted)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {campaign.product}
      </p>
      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          fontSize: 11,
          color: 'var(--color-text-muted)',
        }}
      >
        {voiceId && (
          <span
            style={{
              background: 'var(--color-surface-alt)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
            title={`Voice id ${voiceId}`}
          >
            voice
          </span>
        )}
        {model && (
          <span
            style={{
              background: 'var(--color-surface-alt)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            {model}
          </span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          {new Date(campaign.created_at).toLocaleDateString()}
        </span>
      </div>
    </Link>
  );
}
